import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { SectionLabel } from "../components/ui-kit";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { getSiteSettings } from "../lib/api";
import {
  Check, X, Star, ArrowRight, Copy, Crown, Compass, BookOpen,
  Target, Users, HeartPulse, TrendingUp, ShieldCheck, MessageSquare, MessageCircle,
} from "lucide-react";

const GOLD = "#D4AF37";
const DEFAULT_UPI_ID = "shahmanav456@okhdfcbank";
const PAYEE_NAME = "Manav Fit Coach";
const DEFAULT_WHATSAPP = "919324404572";
const cleanNum = (n) => (n || DEFAULT_WHATSAPP).replace(/[^\d]/g, "");

const PLANS = [
  {
    id: "program",
    icon: BookOpen,
    title: "Training Program",
    subtitle: "Self-Guided Program",
    price: "₹2,999",
    amount: 2999,
    intPrice: "$49",
    small: "One-Time Payment",
    desc: "Perfect for self-motivated individuals who want a professionally designed workout and nutrition program without personal coaching.",
    cta: "Buy Program",
    features: [
      "Professional Workout Program",
      "Nutrition Guide",
      "Exercise Video Library",
      "Warm-up & Mobility Routine",
      "Cardio Guide",
      "Progress Tracker",
      "Supplement Guide",
    ],
    excluded: ["Weekly Check-ins", "Personal Coaching", "Custom Modifications"],
  },
  {
    id: "guided",
    icon: Compass,
    title: "Guided Coaching",
    subtitle: "Weekly Accountability Coaching",
    price: "₹8,999",
    amount: 8999,
    intPrice: "$199",
    small: "or ₹3,499/month × 3",
    desc: "Ideal for individuals who want expert guidance, structured programming, and weekly accountability while following a customized system.",
    cta: "Get Started",
    badge: "Best Value",
    featured: true,
    features: [
      "Goal-Based Training Program",
      "Nutrition Guidelines",
      "Weekly Check-ins",
      "Monthly Program Updates",
      "Progress Tracking",
      "Exercise Video Library",
      "Habit Tracking",
      "Cardio Recommendations",
      "Supplement Guidance",
      "Limited WhatsApp Support",
      "Coach Feedback",
    ],
  },
  {
    id: "elite",
    icon: Crown,
    title: "Elite 1-on-1 Coaching",
    subtitle: "Premium Personal Coaching",
    price: "₹19,999",
    amount: 19999,
    intPrice: "$499",
    small: "or ₹7,499/month × 3",
    desc: "Complete transformation coaching with fully customized training, nutrition, weekly adjustments, priority support and maximum accountability.",
    cta: "Apply Now",
    features: [
      "Fully Customized Workout Program",
      "Personalized Nutrition Plan",
      "Weekly Progress Check-ins",
      "Workout & Diet Adjustments",
      "Exercise Form Analysis",
      "Video Technique Feedback",
      "Cardio Planning",
      "Daily Habit Coaching",
      "Lifestyle Optimization",
      "Supplement Recommendations",
      "Priority WhatsApp Support",
      "Direct Coach Access",
      "Progress Tracking Dashboard",
      "Monthly Goal Review",
      "Travel & Eating Out Guidance",
      "Injury Exercise Modifications",
    ],
  },
];

const buildUPILink = (plan, upiId) => {
  const params = new URLSearchParams({
    pa: upiId || DEFAULT_UPI_ID,
    pn: PAYEE_NAME,
    am: String(plan.amount),
    cu: "INR",
    tn: `${plan.title} - Manav Fit Coach`,
  });
  return `upi://pay?${params.toString()}`;
};

const WHY = [
  { icon: Target, t: "Personalized Coaching" },
  { icon: TrendingUp, t: "Faster Results" },
  { icon: HeartPulse, t: "Sustainable Nutrition" },
  { icon: Users, t: "Weekly Accountability" },
  { icon: MessageSquare, t: "Direct Coach Support" },
  { icon: ShieldCheck, t: "Proven Transformation System" },
];

const FAQ = [
  ["Can I pay monthly?", "Yes. Guided Coaching and Elite Coaching are available with monthly payment options."],
  ["Is the nutrition plan vegetarian friendly?", "Yes. Every nutrition plan is customized according to your dietary preferences and lifestyle."],
  ["Will my workout be customized?", "Yes. Elite Coaching includes a completely personalized workout plan. Guided Coaching follows a goal-based structured plan."],
  ["How long is the coaching?", "All coaching plans are designed for a 12-week transformation."],
];

export default function Pricing() {
  const [copied, setCopied] = useState(false);
  const [payPlan, setPayPlan] = useState(null);
  const [upiId, setUpiId] = useState(DEFAULT_UPI_ID);
  const [waNumber, setWaNumber] = useState(DEFAULT_WHATSAPP);

  useEffect(() => {
    getSiteSettings().then((s) => {
      if (s?.upi_id) setUpiId(s.upi_id);
      if (s?.whatsapp_number) setWaNumber(cleanNum(s.whatsapp_number));
    }).catch(() => {});
  }, []);

  const copyUPI = () => {
    navigator.clipboard.writeText(upiId).then(() => {
      setCopied(true);
      toast.success("UPI ID copied — paste in Google Pay / PhonePe / any UPI app");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div data-testid="pricing-page" className="relative">
      {/* Hero */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-14 overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-30"
             style={{ background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)` }} />
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center relative">
          <Reveal>
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-12" style={{ background: `${GOLD}66` }} />
              <span className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: GOLD }}>Pricing Plans</span>
              <span className="h-px w-12" style={{ background: `${GOLD}66` }} />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] max-w-4xl mx-auto">
              Choose the coaching that&apos;s <span style={{ color: GOLD }}>right for you</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Whether you want complete 1-on-1 coaching or a structured training program, pick the level of support that fits your goals.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {PLANS.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`plan-${p.id}`}
                className={`relative rounded-[20px] p-8 md:p-9 flex flex-col overflow-hidden ${
                  p.featured ? "md:scale-[1.04] shadow-2xl" : "hover:-translate-y-1.5"
                } transition-all duration-500`}
                style={{
                  background: "hsl(var(--card) / 0.6)",
                  backdropFilter: "blur(20px)",
                  border: p.featured ? `2px solid ${GOLD}` : "1px solid hsl(var(--border))",
                  boxShadow: p.featured ? `0 30px 80px -20px ${GOLD}55, 0 0 0 1px ${GOLD}22 inset` : undefined,
                }}
              >
                {p.featured && (
                  <>
                    <div className="absolute -top-px left-0 right-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-black px-4 py-1.5 rounded-full text-black"
                          style={{ background: GOLD }}>
                      <Star className="w-3 h-3 fill-black" /> {p.badge}
                    </span>
                  </>
                )}
                <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                      style={{ background: p.featured ? `${GOLD}22` : "hsl(var(--secondary))" }}>
                  <p.icon className="w-6 h-6" style={{ color: p.featured ? GOLD : "currentColor" }} />
                </span>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">{p.subtitle}</p>
                <h3 className="font-heading font-black text-2xl md:text-3xl leading-tight mb-4">{p.title}</h3>
                <div className="mb-4">
                  <span className="font-heading font-black text-5xl tracking-tight" style={{ color: p.featured ? GOLD : "currentColor" }}>{p.price}</span>
                  <p className="text-xs text-muted-foreground mt-1">{p.small}</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">{p.desc}</p>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
                      <span>{f}</span>
                    </li>
                  ))}
                  {p.excluded?.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground/70">
                      <X className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setPayPlan(p)} data-testid={`plan-cta-${p.id}`}
                  className={`rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] ${
                    p.featured ? "text-black" : "border border-border hover:border-foreground/40"
                  }`}
                  style={p.featured ? { background: GOLD } : {}}>
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* UPI direct pay */}
      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-[20px] p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5"
                 style={{ background: "hsl(var(--card) / 0.5)", backdropFilter: "blur(20px)", border: `1px solid ${GOLD}44` }}
                 data-testid="upi-pay-box">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-1" style={{ color: GOLD }}>Direct UPI Payment</p>
                <p className="font-heading font-black text-xl">Pay directly via Google Pay / PhonePe / any UPI app</p>
                <p className="text-sm text-muted-foreground mt-1">Send to <span className="font-mono font-semibold text-foreground">{upiId}</span></p>
              </div>
              <button onClick={copyUPI} data-testid="upi-copy-btn"
                className="rounded-full px-5 py-3 text-sm font-semibold flex items-center gap-2 text-black hover:scale-105 transition-transform"
                style={{ background: GOLD }}>
                <Copy className="w-4 h-4" /> {copied ? "Copied!" : "Copy UPI ID"}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* International pricing */}
      <section className="py-16 border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <SectionLabel index="02">International Coaching</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">For clients outside India.</h2>
            <p className="text-muted-foreground">Same premium coaching, priced in USD.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {PLANS.map((p) => (
              <Reveal key={p.id}>
                <div className="rounded-[20px] p-6 text-center"
                     style={{ background: "hsl(var(--card) / 0.5)", border: p.featured ? `1px solid ${GOLD}66` : "1px solid hsl(var(--border))" }}>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">{p.title}</p>
                  <p className="font-heading font-black text-4xl" style={{ color: p.featured ? GOLD : "currentColor" }}>{p.intPrice}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-20 border-t border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <SectionLabel index="03">Why Invest</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">Why invest in coaching?</h2>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map((w) => (
              <StaggerItem key={w.t}>
                <div className="rounded-[20px] p-6 flex items-center gap-4 hover-lift"
                     style={{ background: "hsl(var(--card) / 0.5)", backdropFilter: "blur(20px)", border: "1px solid hsl(var(--border))" }}>
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}22` }}>
                    <w.icon className="w-6 h-6" style={{ color: GOLD }} />
                  </span>
                  <p className="font-heading font-bold">{w.t}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <SectionLabel index="04">FAQ</SectionLabel>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">Questions answered.</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="pricing-faq">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-[20px] px-6 border-none"
                style={{ background: "hsl(var(--card) / 0.5)", backdropFilter: "blur(20px)", border: "1px solid hsl(var(--border))" }}>
                <AccordionTrigger className="text-left font-heading font-semibold hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 md:py-32 border-t border-border relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 w-[700px] h-[700px] rounded-full opacity-40 -translate-x-1/2 -translate-y-1/2"
             style={{ background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)` }} />
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center relative">
          <Reveal>
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-6">
              Ready to start your <span style={{ color: GOLD }}>transformation?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto">
              Choose your coaching plan today and take the first step toward a stronger, leaner, healthier version of yourself.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/coaching#coaching-application" data-testid="pricing-final-apply"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-black hover:scale-105 transition-transform"
                style={{ background: GOLD }}>
                Apply for Coaching <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`https://wa.me/${waNumber}?text=Hi%20Manav%2C%20I%27d%20like%20to%20book%20a%20free%20consultation.`} target="_blank" rel="noreferrer" data-testid="pricing-final-consult"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold border border-border hover:border-foreground/40 transition-all">
                Book a Free Consultation
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <PaymentModal plan={payPlan} onClose={() => setPayPlan(null)} />
    </div>
  );
}

function PaymentModal({ plan, onClose, upiId, waNumber }) {
  const upi = upiId || DEFAULT_UPI_ID;
  const wa = waNumber || DEFAULT_WHATSAPP;
  const [copied, setCopied] = useState(false);
  const [paidNotice, setPaidNotice] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(upi).then(() => {
      setCopied(true);
      toast.success("UPI ID copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const waMsg = plan ? `Hi Manav, I just paid ₹${plan.amount} for ${plan.title} via UPI. Attaching my payment screenshot.` : "";
  const paidMsg = plan
    ? `Hi Manav, I've completed my UPI payment for ${plan.title}.\n\nName: (please share)\nEmail: (please share)\n\nI'm ready to start my onboarding.`
    : "";
  const paidURL = `https://wa.me/${wa}?text=${encodeURIComponent(paidMsg)}`;
  const handlePaidClick = () => {
    setPaidNotice(true);
    setTimeout(() => setPaidNotice(false), 6000);
  };
  return (
    <AnimatePresence>
      {plan && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm overflow-y-auto"
          data-testid="pay-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="pay-modal"
            className="relative w-full max-w-xl rounded-[24px] overflow-hidden"
            style={{ background: "hsl(var(--card))", border: `2px solid ${GOLD}` }}
          >
            <button onClick={onClose} data-testid="pay-modal-close" aria-label="Close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/70">
              <X className="w-4 h-4" />
            </button>
            <div className="p-8 text-center">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: GOLD }}>Secure UPI Payment</p>
              <h3 className="font-heading font-black text-2xl md:text-3xl mb-1">{plan.title}</h3>
              <p className="font-heading font-black text-4xl mt-4" style={{ color: GOLD }}>{plan.price}</p>
              <p className="text-xs text-muted-foreground mt-1">{plan.small}</p>

              <div className="mt-6 flex justify-center">
                <div className="rounded-2xl bg-white p-4">
                  <QRCodeSVG value={buildUPILink(plan, upi)} size={220} level="M" includeMargin={false} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Scan with any UPI app · GPay · PhonePe · Paytm · BHIM</p>

              <div className="mt-6 rounded-xl p-4" style={{ background: "hsl(var(--secondary) / 0.5)", border: "1px solid hsl(var(--border))" }}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">UPI ID</p>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono font-semibold">{upi}</span>
                  <button onClick={copy} data-testid="pay-modal-copy" className="rounded-full px-4 py-1.5 text-xs font-semibold text-black hover:scale-105 transition-transform flex items-center gap-1.5" style={{ background: GOLD }}>
                    <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <a href={buildUPILink(plan, upi)} data-testid="pay-modal-upi-btn"
                  className="rounded-full py-3 text-sm font-semibold text-black hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                  style={{ background: GOLD }}>
                  Pay via UPI App <ArrowRight className="w-4 h-4" />
                </a>
                <a href={`https://wa.me/${wa}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer" data-testid="pay-modal-wa-btn"
                  className="rounded-full py-3 text-sm font-semibold border border-border hover:border-foreground/40 flex items-center justify-center gap-2 transition-all">
                  <MessageCircle className="w-4 h-4" /> Share Screenshot
                </a>
              </div>

              <a href={paidURL} target="_blank" rel="noreferrer" onClick={handlePaidClick} data-testid="pay-modal-paid-btn"
                className="mt-4 w-full rounded-full py-3.5 text-sm font-bold flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform text-white"
                style={{ background: "#25D366" }}>
                <MessageCircle className="w-4 h-4" /> I&apos;ve Paid — Continue on WhatsApp
              </a>
              {paidNotice && (
                <p className="mt-2 text-xs text-center" style={{ color: GOLD }} data-testid="pay-modal-paid-notice">
                  WhatsApp opened. Please send the message to complete your payment confirmation.
                </p>
              )}

              <div className="mt-5 text-left rounded-xl p-4 border border-border">
                <p className="text-xs font-heading font-bold uppercase tracking-widest mb-2" style={{ color: GOLD }}>After You Pay</p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>Screenshot the payment confirmation from your UPI app</li>
                  <li>Send it on WhatsApp with your name and email</li>
                  <li>Manav will personally onboard you within 24 hours</li>
                </ol>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
